import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Checkbox, Col, Dropdown, Menu, Row, Space, Typography } from 'antd';
import {
    TbActivity,
    TbArchive,
    TbAt,
    TbBraces,
    TbDownload,
    TbFileTypeCsv,
    TbMessageCircle,
    TbMessageOff,
    TbPencil,
    TbSettings2,
    TbSortAscending,
    TbSortDescending,
    TbUserCog,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useResourceActivityExportAccess } from '@/hooks/useResourceActivityExportAccess';
import { resourceActivityService, resourceCommentService } from '@/services/database';
import { ActivityTimelineSkeleton, DropdownOptionContent, EmptySection, SmallTooltip, VirtualizedList } from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useUserStore } from '@/stores';
import {
    CommentComposerValue,
    ResourceActivity,
    ResourceActivityEventType,
    ResourceActivityNotificationPreferenceKey,
    ResourceActivityNotificationPreferences,
    ResourceActivityResourceType,
    ResourceActivitySubscriber,
    ResourceComment,
} from '@/types';

import { subscribeResourceActivityEvents } from '../resourceActivityEvents';
import { childCommentsFor, upsertComment } from '../ResourceCommentCard/utils';
import {
    emitOptimisticResourceCommentCreated,
    removeOptimisticResourceComment,
    replaceOptimisticResourceComment,
} from '../resourceCommentEvents';
import ResourceActivityFeedItem from './components/ResourceActivityFeedItem';
import {
    ActivityExportFormat,
    activityMatchesResource,
    activityToCsv,
    defaultNotificationPreferences,
    downloadBlob,
    iconStyle,
    labelForResourceType,
    ResourceActivityOrder,
    upsertActivity,
} from './utils';

interface ResourceActivityFeedProps {
    authorizationResourceId?: string;
    canExport?: boolean;
    height?: number | string;
    includeNested?: boolean;
    pageSize?: number;
    resourceId: string;
    resourceTitle?: ReactNode;
    resourceType: ResourceActivityResourceType;
    resourceWebsite?: string;
}

const ResourceActivityFeed: FC<ResourceActivityFeedProps> = ({
    authorizationResourceId,
    canExport,
    height = '28rem',
    includeNested = true,
    pageSize = 25,
    resourceId,
    resourceTitle,
    resourceType,
    resourceWebsite,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { selectedUser: user } = useUserStore();
    const hasDerivedExportAccess = useResourceActivityExportAccess(resourceType, resourceId, authorizationResourceId);
    const canExportActivities = canExport ?? hasDerivedExportAccess;
    const [activities, setActivities] = useState<ResourceActivity[]>([]);
    const [activityComments, setActivityComments] = useState<ResourceComment[]>([]);
    const [subscribers, setSubscribers] = useState<ResourceActivitySubscriber[]>([]);
    const [cursor, setCursor] = useState<string>();
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [order, setOrder] = useState<ResourceActivityOrder>('oldest');
    const [showComments, setShowComments] = useState(true);
    const [activitySettingsOpen, setActivitySettingsOpen] = useState(false);
    const [submittingActivityCommentReplyId, setSubmittingActivityCommentReplyId] = useState<string>();
    const [updatingSubscription, setUpdatingSubscription] = useState(false);

    const activeSubscriber = useMemo(
        () => subscribers.find((subscriber) => subscriber.member?.id === user?.activeMember?.id),
        [subscribers, user?.activeMember?.id],
    );
    const subscribed = activeSubscriber?.subscribed ?? true;
    const notificationPreferences = useMemo(
        () => ({
            ...defaultNotificationPreferences,
            ...(activeSubscriber?.notificationPreferences ?? {}),
        }),
        [activeSubscriber?.notificationPreferences],
    );
    const resourceLabel = labelForResourceType(resourceType);

    const loadSubscribers = useCallback(async () => {
        try {
            setSubscribers(await resourceActivityService.listSubscribers(resourceType, resourceId));
        } catch (error) {
            captureException(error);
        }
    }, [resourceId, resourceType]);

    const loadInitialActivities = useCallback(async () => {
        if (!resourceId) {
            return;
        }

        try {
            setLoading(true);
            setCursor(undefined);
            setHasMore(true);

            const page = await resourceActivityService.list(resourceType, resourceId, {
                includeNested,
                limit: pageSize,
                order,
            });

            setActivities(page.items);
            setCursor(page.nextCursor);
            setHasMore(page.hasMore);
        } catch (error) {
            captureException(error);
        } finally {
            setLoading(false);
        }
    }, [includeNested, order, pageSize, resourceId, resourceType]);

    const loadMoreActivities = useCallback(async () => {
        if (!resourceId || !hasMore || loadingMore) {
            return;
        }

        try {
            setLoadingMore(true);

            const page = await resourceActivityService.list(resourceType, resourceId, {
                cursor,
                includeNested,
                limit: pageSize,
                order,
            });

            setActivities((previous) => [...previous, ...page.items]);
            setCursor(page.nextCursor);
            setHasMore(page.hasMore);
        } catch (error) {
            captureException(error);
        } finally {
            setLoadingMore(false);
        }
    }, [cursor, hasMore, includeNested, loadingMore, order, pageSize, resourceId, resourceType]);

    useEffect(() => {
        loadInitialActivities();
        loadSubscribers();
        resourceActivityService.markRead(resourceType, resourceId).catch(captureException);
    }, [loadInitialActivities, loadSubscribers, resourceId, resourceType]);

    useEffect(() => {
        return subscribeResourceActivityEvents((detail) => {
            if (detail.action === 'deleted' && detail.activityId) {
                setActivities((previous) => previous.filter((activity) => activity.id !== detail.activityId));
                return;
            }

            if (
                !detail.activity ||
                !activityMatchesResource(detail.activity, resourceType, resourceId, includeNested)
            ) {
                return;
            }

            setActivities((previous) => upsertActivity(previous, detail.activity as ResourceActivity, order));
        });
    }, [includeNested, order, resourceId, resourceType]);

    const upsertSubscriber = useCallback((subscriber: ResourceActivitySubscriber) => {
        setSubscribers((previous) => {
            const existingSubscriber = previous.some((item) => item.id === subscriber.id);

            return existingSubscriber
                ? previous.map((item) => (item.id === subscriber.id ? subscriber : item))
                : [subscriber, ...previous];
        });
    }, []);

    const updateSubscription = async (input: {
        notificationPreferences?: ResourceActivityNotificationPreferences;
        subscribed?: boolean;
    }) => {
        try {
            setUpdatingSubscription(true);

            const subscriber = await resourceActivityService.updateSubscription(resourceType, resourceId, input);

            upsertSubscriber(subscriber);
        } catch (error) {
            captureException(error);
            toast.error('Could not update activity subscription');
        } finally {
            setUpdatingSubscription(false);
        }
    };

    const toggleNotificationPreference = (preference: ResourceActivityNotificationPreferenceKey) => {
        const nextPreferences = {
            ...notificationPreferences,
            [preference]: !(subscribed && notificationPreferences[preference]),
        };

        return updateSubscription({
            notificationPreferences: {
                ...nextPreferences,
            },
            subscribed: Object.values(nextPreferences).some(Boolean),
        });
    };

    const exportActivities = async (format: ActivityExportFormat) => {
        if (!canExportActivities) {
            return;
        }

        const label = format === 'json' ? 'JSON' : 'CSV';
        const toastId = toast.loading(`Downloading ${label}`, {
            description: 'Preparing your activity export.',
        });

        try {
            const activityBlob = await resourceActivityService.export(resourceType, resourceId);

            if (format === 'json') {
                downloadBlob(activityBlob, `${resourceType}-${resourceId}-activity.json`);

                toast.success(`${label} Download Started`, {
                    id: toastId,
                    description: 'Your export should appear in your downloads shortly.',
                });

                return;
            }

            const exportedActivities = JSON.parse(await activityBlob.text()) as ResourceActivity[];
            const csvBlob = new Blob([activityToCsv(exportedActivities)], {
                type: 'text/csv;charset=utf-8',
            });

            downloadBlob(csvBlob, `${resourceType}-${resourceId}-activity.csv`);

            toast.success(`${label} Download Started`, {
                id: toastId,
                description: 'Your export should appear in your downloads shortly.',
            });
        } catch (error) {
            captureException(error);
            toast.error('Could not export activity', {
                id: toastId,
            });
        }
    };

    const createActivityCommentReply = async (parentComment: ResourceComment, value: CommentComposerValue) => {
        const input = {
            component: parentComment.component,
            mentions: value.mentions,
            message: value.message,
            metadata: {
                source: 'resource-comment-thread',
                target: {
                    parentCommentId: parentComment.id,
                    type: 'comment-reply',
                },
            },
            parentId: parentComment.id,
        };
        const optimisticComment = emitOptimisticResourceCommentCreated(
            parentComment.resourceType,
            parentComment.resourceId,
            input,
        );

        setActivityComments((previous) => upsertComment(previous, optimisticComment));

        try {
            setSubmittingActivityCommentReplyId(parentComment.id);

            const comment = await resourceCommentService.create(
                parentComment.resourceType,
                parentComment.resourceId,
                input,
            );

            replaceOptimisticResourceComment(
                optimisticComment,
                comment,
                parentComment.resourceId,
                parentComment.resourceType,
            );
            setActivityComments((previous) =>
                upsertComment(
                    previous.filter((item) => item.id !== optimisticComment.id),
                    comment,
                ),
            );
            return true;
        } catch (error) {
            removeOptimisticResourceComment(optimisticComment, parentComment.resourceId, parentComment.resourceType);
            setActivityComments((previous) => previous.filter((item) => item.id !== optimisticComment.id));
            captureException(error);
            toast.error('Could not create reply');
            return false;
        } finally {
            setSubmittingActivityCommentReplyId(undefined);
        }
    };

    const visibleActivities = useMemo(
        () =>
            showComments
                ? activities
                : activities.filter((activity) => activity.eventType !== ResourceActivityEventType.COMMENTED),
        [activities, showComments],
    );
    const commentActivityIds = useMemo(
        () =>
            visibleActivities
                .filter((activity) => activity.eventType === ResourceActivityEventType.COMMENTED)
                .map((activity) => activity.metadata?.commentId)
                .filter((commentId): commentId is string => typeof commentId === 'string' && commentId.length > 0),
        [visibleActivities],
    );
    const commentActivityKey = commentActivityIds.join('|');
    const activityCommentsById = useMemo(
        () =>
            activityComments.reduce<Record<string, ResourceComment>>((accumulator, comment) => {
                accumulator[comment.id] = comment;

                return accumulator;
            }, {}),
        [activityComments],
    );

    useEffect(() => {
        if (!showComments || !commentActivityKey) {
            setActivityComments([]);
            return;
        }

        let cancelled = false;

        resourceCommentService
            .list(resourceType, resourceId, { includeResolved: true })
            .then((comments) => {
                if (!cancelled) {
                    setActivityComments(comments);
                }
            })
            .catch(captureException);

        return () => {
            cancelled = true;
        };
    }, [commentActivityKey, resourceId, resourceType, showComments]);

    const activityOrderOptions = [
        {
            description: 'Newest activity appears at the bottom.',
            icon: <TbSortAscending size='1rem' style={{ ...iconStyle, color: token.colorTextTertiary }} />,
            label: 'Oldest First',
            value: 'oldest' as ResourceActivityOrder,
        },
        {
            description: 'Newest activity appears at the top.',
            icon: <TbSortDescending size='1rem' style={{ ...iconStyle, color: token.colorTextTertiary }} />,
            label: 'Newest First',
            value: 'newest' as ResourceActivityOrder,
        },
    ];
    const selectedActivityOrder = activityOrderOptions.find((option) => option.value === order);

    const activityOrderMenu = (
        <DropdownMenuPanel selectedKeys={[order]} style={{ minWidth: 16 * 16 }}>
            <div style={{ padding: '0 0.75rem', marginBottom: '0.25rem' }}>
                <small style={{ color: token.colorTextTertiary }}>Order</small>
            </div>
            {activityOrderOptions.map((option) => (
                <Menu.Item key={option.value} onClick={() => setOrder(option.value)}>
                    <DropdownOptionContent
                        description={option.description}
                        icon={option.icon}
                        selected={order === option.value}
                        title={option.label}
                    />
                </Menu.Item>
            ))}
        </DropdownMenuPanel>
    );
    const subscriptionOptions = [
        {
            description: 'Notify me when I am mentioned directly.',
            icon: <TbAt size='1rem' style={iconStyle} />,
            key: 'mentions',
            label: 'Mentions',
        },
        {
            description: 'New comments and replies on this resource.',
            icon: <TbMessageCircle size='1rem' style={iconStyle} />,
            key: 'comments',
            label: 'Comments',
        },
        {
            description: 'Collaborators or permissions change.',
            icon: <TbUserCog size='1rem' style={iconStyle} />,
            key: 'collaboration',
            label: 'Collaborators',
        },
        {
            description: `Edits to this ${resourceLabel} or its nested content.`,
            icon: <TbPencil size='1rem' style={iconStyle} />,
            key: 'content',
            label: 'Content Updates',
        },
        {
            description: 'Created, archived, restored, or deleted events.',
            icon: <TbArchive size='1rem' style={iconStyle} />,
            key: 'lifecycle',
            label: 'Status Changes',
        },
        {
            description: 'Export and download events.',
            icon: <TbDownload size='1rem' style={iconStyle} />,
            key: 'exports',
            label: 'Downloads',
        },
    ] as const;
    const activitySettingsMenu = (
        <DropdownMenuPanel style={{ minWidth: 16 * 20 }}>
            <div style={{ padding: '0 0.75rem', marginBottom: '0.25rem' }}>
                <small style={{ color: token.colorTextTertiary }}>Inbox Notifications</small>
            </div>
            {subscriptionOptions.map((option) => (
                <Menu.Item
                    key={option.key}
                    disabled={updatingSubscription}
                    onClick={({ domEvent }) => {
                        domEvent.stopPropagation();
                        toggleNotificationPreference(option.key);
                        window.setTimeout(() => setActivitySettingsOpen(true), 0);
                    }}
                >
                    <DropdownOptionContent
                        control={
                            <Checkbox
                                checked={subscribed && notificationPreferences[option.key]}
                                onChange={() => undefined}
                            />
                        }
                        description={option.description}
                        icon={option.icon}
                        title={option.label}
                    />
                </Menu.Item>
            ))}
            {canExportActivities && (
                <>
                    <Menu.Divider />
                    <div style={{ padding: '0 0.75rem', marginBottom: '0.25rem' }}>
                        <small style={{ color: token.colorTextTertiary }}>Download</small>
                    </div>
                    <Menu.Item
                        key='json'
                        onClick={() => {
                            setActivitySettingsOpen(false);
                            exportActivities('json');
                        }}
                    >
                        <DropdownOptionContent
                            icon={<TbBraces size='1rem' />}
                            title={
                                <>
                                    JSON
                                    <small style={{ color: token.colorTextTertiary, marginLeft: '0.5rem' }}>
                                        .json
                                    </small>
                                </>
                            }
                        />
                    </Menu.Item>
                    <Menu.Item
                        key='csv'
                        onClick={() => {
                            setActivitySettingsOpen(false);
                            exportActivities('csv');
                        }}
                    >
                        <DropdownOptionContent
                            icon={<TbFileTypeCsv size='1rem' />}
                            title={
                                <>
                                    CSV
                                    <small style={{ color: token.colorTextTertiary, marginLeft: '0.5rem' }}>.csv</small>
                                </>
                            }
                        />
                    </Menu.Item>
                </>
            )}
        </DropdownMenuPanel>
    );

    const activityItems = visibleActivities.map((activity, index) => {
        const isLastActivity = index === visibleActivities.length - 1;
        const commentId =
            typeof activity.metadata?.commentId === 'string' && activity.metadata.commentId.length > 0
                ? activity.metadata.commentId
                : undefined;
        const activityComment =
            activity.eventType === ResourceActivityEventType.COMMENTED && commentId
                ? activityCommentsById[commentId]
                : undefined;
        const activityCommentReplies = activityComment ? childCommentsFor(activityComments, activityComment.id) : [];

        return (
            <ResourceActivityFeedItem
                key={activity.id}
                activity={activity}
                activityComment={activityComment}
                activityCommentReplies={activityCommentReplies}
                isLastActivity={isLastActivity}
                onCreateReply={createActivityCommentReply}
                resourceTitle={typeof resourceTitle === 'string' ? resourceTitle : undefined}
                resourceWebsite={resourceWebsite}
                submittingReply={Boolean(activityComment && submittingActivityCommentReplyId === activityComment.id)}
            />
        );
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height, minHeight: 0 }}>
            <Row align='middle' wrap={false} style={{ marginBottom: '0.75rem' }}>
                <Col flex='auto' style={{ minWidth: 0 }}>
                    <Typography.Title
                        level={5}
                        style={{
                            alignItems: 'center',
                            display: 'flex',
                            lineHeight: 1,
                            marginBottom: 0,
                        }}
                    >
                        <TbActivity size='1.125rem' style={{ flex: 'none', marginRight: '0.375rem' }} />
                        Activity
                    </Typography.Title>
                </Col>
                <Col flex='none'>
                    <Space.Compact size='small'>
                        <SmallTooltip
                            placement='bottomRight'
                            showInfo={false}
                            title={showComments ? 'Hide Comments in Activity' : 'Show Comments in Activity'}
                        >
                            <Button
                                aria-label={showComments ? 'Hide comments in activity' : 'Show comments in activity'}
                                size='small'
                                type={showComments ? 'default' : 'primary'}
                                icon={
                                    showComments ? (
                                        <TbMessageOff size='1rem' style={{ color: token.colorTextTertiary }} />
                                    ) : (
                                        <TbMessageCircle size='1rem' style={{ color: token.colorTextTertiary }} />
                                    )
                                }
                                onClick={() => setShowComments((previous) => !previous)}
                            />
                        </SmallTooltip>
                        <Dropdown trigger={['click']} placement='bottomRight' popupRender={() => activityOrderMenu}>
                            <DropdownButton size='small' icon={selectedActivityOrder?.icon}>
                                {selectedActivityOrder?.label}
                            </DropdownButton>
                        </Dropdown>
                        <Dropdown
                            trigger={['click']}
                            placement='bottomRight'
                            popupRender={() => activitySettingsMenu}
                            open={activitySettingsOpen}
                            onOpenChange={setActivitySettingsOpen}
                        >
                            <Button
                                size='small'
                                loading={updatingSubscription}
                                icon={<TbSettings2 size='1rem' style={{ color: token.colorTextTertiary }} />}
                            />
                        </Dropdown>
                    </Space.Compact>
                </Col>
            </Row>
            <div
                style={{
                    border: `1px dashed ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    boxSizing: 'border-box',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    padding: '0.75rem 0.75rem 0.75rem 0.5rem',
                }}
            >
                {loading ? (
                    <div style={{ height: '100%', overflow: 'auto', paddingRight: '0.25rem' }}>
                        <ActivityTimelineSkeleton count={6} style={{ minHeight: '100%', padding: '0.125rem 0' }} />
                    </div>
                ) : (
                    <VirtualizedList
                        emptyStyle={{ boxSizing: 'border-box', height: '100%', padding: 0 }}
                        hasMore={hasMore}
                        loadingMore={loadingMore}
                        onEndReached={loadMoreActivities}
                        overflowFade
                        overflowFadeSurface='container'
                        style={{ height: '100%', overflow: 'auto', paddingRight: '0.25rem' }}
                        loadMore={
                            loadingMore ? (
                                <div style={{ padding: '0.75rem 0' }}>
                                    <ActivityTimelineSkeleton count={2} />
                                </div>
                            ) : null
                        }
                        locale={{
                            emptyText: (
                                <EmptySection
                                    height='100%'
                                    icon={<TbActivity size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                                    label='No Activity'
                                    description='Workspace activity will appear here as this resource changes.'
                                />
                            ),
                        }}
                    >
                        {visibleActivities.length > 0 ? (
                            <div style={{ padding: '0.125rem 0 0.25rem' }}>{activityItems}</div>
                        ) : (
                            <EmptySection
                                height='100%'
                                icon={<TbActivity size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                                label='No Activity'
                                description='Workspace activity will appear here as this resource changes.'
                            />
                        )}
                    </VirtualizedList>
                )}
            </div>
            {!showComments && (
                <small style={{ color: token.colorTextTertiary, flex: 'none', marginTop: '0.5rem' }}>
                    Comment events are hidden from this activity view.
                </small>
            )}
        </div>
    );
};

export default ResourceActivityFeed;
